import { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Zap, MapPin, Users, Car, Clock, Shield } from "lucide-react";

const stats = [
  {
    icon: Zap,
    value: 50,
    suffix: "+",
    label: "Charging Stations",
    description: "Across India",
    color: "primary",
    image: "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=400&h=300&fit=crop",
  },
  {
    icon: MapPin,
    value: 15,
    suffix: "+",
    label: "Cities Covered",
    description: "Growing network",
    color: "secondary",
  },
  {
    icon: Users,
    value: 10000,
    suffix: "+",
    label: "Happy Customers",
    description: "And counting",
    color: "primary",
  },
  {
    icon: Car,
    value: 50000,
    suffix: "+",
    label: "Charges Delivered",
    description: "Monthly sessions",
    color: "secondary",
  },
];

const features = [
  {
    icon: Clock,
    title: "24/7 Availability",
    description: "All stations operational round the clock",
  },
  {
    icon: Shield,
    title: "Safe & Secure",
    description: "Advanced safety protocols at every station",
  },
];

const CountUp = ({ end, suffix = "", duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!isInView) return;

    let startTime;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }, [isInView, end, duration]);

  return (
    <span ref={ref}>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

const Stats = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  return (
    <section className="py-24 md:py-32 bg-background relative overflow-hidden" data-testid="stats-section">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-[100px] translate-x-1/2 translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24" ref={containerRef}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            Our Impact
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
            THE ELECTRIC REVOLUTION
            <br />
            <span className="text-primary">IN NUMBERS</span>
          </h2>
        </motion.div>

        {/* Stats Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`glass-card group ${
                index === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
              data-testid={`stat-card-${index}`}
            >
              <div
                className={`${
                  index === 0 ? "h-full flex flex-col justify-between" : ""
                }`}
              >
                <div
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-all group-hover:scale-110 ${
                    stat.color === "primary"
                      ? "bg-primary/10 text-primary"
                      : "bg-secondary/10 text-secondary"
                  }`}
                >
                  <stat.icon className="w-7 h-7" />
                </div>
                <div>
                  <p
                    className={`font-heading ${
                      index === 0 ? "text-6xl md:text-7xl" : "text-4xl md:text-5xl"
                    } ${
                      stat.color === "primary" ? "text-primary" : "text-secondary"
                    }`}
                  >
                    <CountUp end={stat.value} suffix={stat.suffix} />
                  </p>
                  <p className="text-xl font-medium text-foreground mt-2">
                    {stat.label}
                  </p>
                  <p className="text-foreground-muted text-sm mt-1">
                    {stat.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
              className="glass-card flex items-center gap-6"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                <feature.icon className="w-8 h-8 text-accent" />
              </div>
              <div>
                <h3 className="text-xl font-heading text-foreground">
                  {feature.title}
                </h3>
                <p className="text-foreground-muted mt-1">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
